import { z } from "zod";
import { zfd } from "zod-form-data";
import { extendZodWithOpenApi } from "zod-openapi";
import { apiLink, formBody, ind, jsonBody, privateApi, successResponse, tagPaths, unref } from "../util.ts";
import {
    billing_account_id,
    created_at_tz,
    disk_uuid,
    simpleSuccessResponse,
    storage_pool_uuid,
    updated_at_tz,
    user_id,
    uuid,
} from "./common.ts";
import * as params from "./params.ts";
import { VirtualMachineStorage } from "./vm.ts";

extendZodWithOpenApi(z);

export const DiskStatusEnum = z.enum(["Active", "Reserved"]).openapi({
    ref: "DiskStatusEnum",
});

export const DiskSourceImageTypeEnum = z
    .enum(["OS_BASE", "DISK", "SNAPSHOT", "EMPTY"])
    .describe("How the disk is initialized")
    .openapi({
        ref: "DiskSourceImageTypeEnum",
    });
//

export const Disk = z
    .object({
        uuid,
        status: DiskStatusEnum,
        snapshots: z
            .array(
                z.object({
                    uuid,
                    size_gb: z.number(),
                    created_at: created_at_tz,
                    disk_uuid,
                }),
            )
            .describe("Snapshots created from this disk")
            .optional(),
        user_id,
        billing_account_id,
        size_gb: z.number(),
        source_image: z
            .string()
            .describe("OS image slug or source disk/snapshot UUID used to populate the disk")
            .optional(),
        source_image_type: DiskSourceImageTypeEnum,
        created_at: created_at_tz,
        updated_at: updated_at_tz.optional(),
        display_name: z.string().optional(),
        storage_pool_uuid: unref(storage_pool_uuid).describe("Storage pool that stores the disk"),
        read_only_bootable: z.boolean().describe("Whether the disk is exposed as read-only boot media").optional(),
    })
    .openapi({ ref: "Disk" });

export const DiskListResponse = z.array(Disk).openapi({ ref: "DiskListResponse" });

export const DiskCreateBody = z
    .object({
        display_name: Disk.shape.display_name,
        billing_account_id: zfd.numeric(billing_account_id),
        size_gb: zfd.numeric(z.number().optional()),
        source_image_type: DiskSourceImageTypeEnum.optional(),
        source_image: z
            .string()
            .describe("OS image slug or source disk/snapshot UUID. Omit it only when `source_image_type` is `EMPTY`.")
            .optional(),
        storage_pool_uuid: unref(storage_pool_uuid)
            .describe("Storage pool to place the new disk into. If omitted, the default pool is used.")
            .optional(),
    })
    .openapi({ ref: "DiskCreateBody" });

export const DiskUpdateBody = z
    .object({
        display_name: Disk.shape.display_name.optional(),
        billing_account_id: zfd.numeric(billing_account_id.optional()),
        read_only_bootable: z.boolean().optional().describe("Mark or unmark disk as read-only bootable media"),
    })
    .openapi({ ref: "DiskUpdateBody" });

export const DiskAttachDetachBody = z
    .object({
        uuid,
        storage_uuid: disk_uuid,
    })
    .openapi({ ref: "DiskAttachDetachBody" });

export const diskPaths = tagPaths("block_storage")({
    "/{location}/storage/disks": {
        get: {
            summary: "List disks",
            parameters: [
                params.location,
                z
                    .boolean()
                    .optional()
                    .describe("Only return disks marked as read-only bootable")
                    .openapi({ param: { name: "read_only_bootable", in: "query" } }),
            ],
            responses: {
                ...successResponse(DiskListResponse),
            },
        },
        post: {
            summary: `Create disk ${privateApi}`,
            description: ind`
            Creates a new block storage (aka disk). It can be initiated as an empty disk, or it can be a copy of an OS base image, an existing disk or a snapshot.
            If \`source_image\` is set then the new image will be a copy of the image referred here. \`source_image\` is required if \`source_image_type\` is something else than \`EMPTY\`. For \`OS_BASE\` it must be something like \`ubuntu_20.04\`. For \`DISK\` or \`SNAPSHOT\` it is the UUID of the respective resource.

            If the OS supports it, a device ID will be constructed from new disk UUID and assigned on OS level, in the same way as described in [Attach disk call](${apiLink("/{location}/user-resource/vm/storage/attach", "block_storage", "post")})`,
            parameters: [params.location],
            requestBody: formBody(DiskCreateBody),
            responses: {
                ...successResponse(Disk),
            },
        },
    },
    "/{location}/storage/disks/{uuid}": {
        get: {
            summary: "Get disk",
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(Disk),
            },
        },
        patch: {
            summary: "Update disk",
            description: "Modify attributes of a disk (such as its display name or billing account).",
            parameters: [params.location, params.uuid],
            requestBody: formBody(DiskUpdateBody),
            responses: {
                ...successResponse(Disk),
            },
        },
        delete: {
            summary: "Delete disk",
            description: "Delete a disk and all its snapshots. All its data will be lost irrecoverably.",
            parameters: [params.location, params.uuid],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
    "/{location}/user-resource/vm/storage/attach": {
        post: {
            summary: "Attach disk",
            description: ind`
            Attaches a disk to a virtual machine.

            If the OS supports assigning a device ID from a specified serial number, the OS disk identifier will be based on first 20 symbols of the disk UUID, with an additional \`virtio-\` prefix. For example: \`/dev/disk/by-id/virtio-3d91aa31-16ec-44ee-b\``,
            parameters: [params.location],
            requestBody: jsonBody(DiskAttachDetachBody),
            responses: {
                ...successResponse(VirtualMachineStorage),
            },
        },
    },
    "/{location}/user-resource/vm/storage/detach": {
        post: {
            summary: "Detach disk",
            description: "Detaches a disk from a virtual machine.",
            parameters: [params.location],
            requestBody: jsonBody(DiskAttachDetachBody),
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
});
